import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import { Icon } from '@shared/icons';
import { Translate } from '@shared/translation';
import { Fragment } from 'react';
import { useAggregation } from '../aggregation/AggregationContext';
import { AggregationAction } from '../aggregation/AggregationSelector';
import { useDataProvider } from '../DataProviderContext';
import { ActiveFilters } from '../filter/ActiveFilters';
import { useIsFilterVisible } from '../filter/FilterContext';
import { useSettings } from '../Settings';
import { GroupByAction } from './GroupByAction';

const Root = styled('div', { name: 'Toolbar', slot: 'Root' })(({ theme }) => ({
  position: 'sticky',
  top: 0,
  left: 0,
  zIndex: 10,
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

const Left = styled('div', { name: 'Toolbar', slot: 'Left' })(({ theme }) => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Right = styled('div', { name: 'Toolbar', slot: 'Right' })(
  ({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    flexFlow: 'row nowrap',
    flex: 1,
    gap: theme.spacing(1),
  })
);

export const useIsToolbarVisible = () => {
  const context = useDataProvider();
  const { showFilterAction, showActiveFilters, showGroupAction } =
    useSettings();
  const aggregation = useAggregation();
  const isAggregationsActive =
    aggregation !== undefined && !aggregation.isFixed;

  const isFilterActive =
    context.table?.options.enableFilters &&
    (showFilterAction || showActiveFilters);
  const isGroupActive =
    context.table?.options.enableGrouping && showGroupAction;

  return (
    isFilterActive ||
    isAggregationsActive ||
    isGroupActive ||
    Boolean(context.toolbarControls) ||
    context.actions?.length > 0
  );
};

export const Toolbar = () => {
  const context = useDataProvider();
  const isVisible = useIsToolbarVisible();
  const { showFilterAction, showActiveFilters, showGroupAction } =
    useSettings();

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Root>
        <Left>
          {showFilterAction && <FilterAction />}
          {showActiveFilters && <ActiveFilters />}
        </Left>
        <Right>
          {context.actions.map((action) => (
            <Button
              onClick={action.onClick}
              variant="contained"
              color="grey"
              sx={{ whiteSpace: 'nowrap' }}
              key={action.id}
            >
              <Translate text={action.displayName} />
            </Button>
          ))}
          <AggregationAction />
          {context.toolbarControls}
          {showGroupAction && <GroupByAction />}
        </Right>
      </Root>
      {context.actions.map((action) => (
        <Fragment key={action.id}>{action.dialog}</Fragment>
      ))}
    </>
  );
};

const FilterAction = () => {
  const { table } = useDataProvider();
  const [isFilterVisible, setIsFilterVisible] = useIsFilterVisible();

  if (!table?.options.enableFilters) {
    return null;
  }

  return (
    <ToggleButton
      value=""
      size="small"
      selected={isFilterVisible}
      onClick={() => setIsFilterVisible(!isFilterVisible)}
    >
      <Icon name="filter" size="small" />
    </ToggleButton>
  );
};
